import { useState, useEffect, useRef, useCallback } from 'react'
import {
  LayoutTemplate,
  Play,
  ExternalLink,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Check,
  X,
} from 'lucide-react'
import {
  getGalleryTemplates,
  getGalleryTemplate,
  createFromTemplate,
  startApp,
  type App,
  type TemplatePreviewImage,
} from '../lib/api'
import { appSlugFromName, finalizeAppSlug, isValidAppSlug, normalizeAppSlugInput } from '../lib/appIdentity'
import { useBrokenImages } from '../hooks/useBrokenImages'

interface TemplateGalleryProps {
  onSelectTemplate: (app: App) => void
  onClose?: () => void
  searchQuery?: string
  onViewChange?: (view: { mode: 'grid' } | { mode: 'detail'; name: string }) => void
  externalBackToGrid?: number  // increment to trigger backToGrid from parent
  initialTemplateName?: string  // Auto-open this template's detail on mount
}

type ViewMode = 'grid' | 'detail'

interface TemplateDetail extends App {
  preview_images: TemplatePreviewImage[]
}

export default function TemplateGallery({ onSelectTemplate, onClose, searchQuery = '', onViewChange, externalBackToGrid, initialTemplateName }: TemplateGalleryProps) {
  const [templates, setTemplates] = useState<App[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  // Use Template flow
  const [showNameInput, setShowNameInput] = useState(false)
  const [appDisplayName, setAppDisplayName] = useState('')
  const [slugInput, setSlugInput] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Live Preview flow
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  // Image carousel
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const { isBroken, markBroken } = useBrokenImages()

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    loadTemplates()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-derive slug from display name
  useEffect(() => {
    if (!slugEdited && appDisplayName) {
      setSlugInput(appSlugFromName(appDisplayName))
    }
  }, [appDisplayName, slugEdited])

  // Respond to parent requesting back-to-grid
  useEffect(() => {
    if (externalBackToGrid && externalBackToGrid > 0) {
      backToGrid()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalBackToGrid])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [])

  async function loadTemplates() {
    setLoading(true)
    setError(null)
    try {
      const data = await getGalleryTemplates()
      setTemplates(data)
      // Auto-open initial template if specified
      if (initialTemplateName) {
        const match = data.find(t => t.name === initialTemplateName)
        if (match) openDetail(match)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  async function openDetail(template: App) {
    setDetailLoading(true)
    setViewMode('detail')
    setCurrentImageIndex(0)
    setShowNameInput(false)
    setCreateError(null)
    setPreviewError(null)
    setAppDisplayName('')
    setSlugInput('')
    setSlugEdited(false)
    onViewChange?.({ mode: 'detail', name: template.display_name || template.name })
    try {
      const detail = await getGalleryTemplate(template.name)
      setSelectedTemplate(detail)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template details')
      setViewMode('grid')
      onViewChange?.({ mode: 'grid' })
    } finally {
      setDetailLoading(false)
    }
  }

  function backToGrid() {
    setViewMode('grid')
    onViewChange?.({ mode: 'grid' })
    setSelectedTemplate(null)
    setShowNameInput(false)
    setCreateError(null)
    setPreviewError(null)
    if (pollRef.current) {
      clearTimeout(pollRef.current)
      pollRef.current = null
    }
  }

  // "Use This Template" flow
  const appName = finalizeAppSlug(slugInput)
  const isNameValid = appDisplayName.trim().length > 0 && isValidAppSlug(appName)

  async function handleUseTemplate(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedTemplate || !isNameValid) return

    setCreating(true)
    setCreateError(null)
    try {
      const newApp = await createFromTemplate(
        selectedTemplate.name,
        appName,
        appDisplayName || undefined,
      )
      onSelectTemplate(newApp)
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create app from template')
    } finally {
      setCreating(false)
    }
  }

  // "Live Preview" flow
  const handleLivePreview = useCallback(async () => {
    if (!selectedTemplate) return
    setPreviewError(null)

    // Cancel any existing poll before starting a new one
    if (pollRef.current) {
      clearTimeout(pollRef.current)
      pollRef.current = null
    }

    const templateStatus = selectedTemplate.status
    if (templateStatus === 'running') {
      window.open(selectedTemplate.url, '_blank')
      return
    }

    // Need to start the container
    setPreviewLoading(true)
    try {
      await startApp(selectedTemplate.name)
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Failed to start preview')
      // Don't set previewLoading false here — let poll completion handle it
      // unless we're not going to poll at all (error means we stop)
      setPreviewLoading(false)
      return
    }

    // Poll until running or timeout
    const startTime = Date.now()
    const TIMEOUT = 60000

    function pollStatus() {
      if (Date.now() - startTime > TIMEOUT) {
        setPreviewLoading(false)
        setPreviewError('Preview timed out - template took too long to start')
        return
      }

      pollRef.current = setTimeout(async () => {
        try {
          const detail = await getGalleryTemplate(selectedTemplate!.name)
          setSelectedTemplate(detail)
          if (detail.status === 'running') {
            setPreviewLoading(false)
            window.open(detail.url, '_blank')
          } else if (detail.status === 'error') {
            setPreviewLoading(false)
            setPreviewError('Preview failed to start')
          } else {
            pollStatus()
          }
        } catch {
          pollStatus()
        }
      }, 2000)
    }

    pollStatus()
  }, [selectedTemplate])

  // Image carousel helpers
  const allImages: string[] = selectedTemplate
    ? [
        ...(selectedTemplate.template_hero_image ? [selectedTemplate.template_hero_image] : []),
        ...(selectedTemplate.preview_images || [])
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((img) => img.image_url)
          .filter((url) => url !== selectedTemplate.template_hero_image),
      ].filter((url) => !isBroken(url))
    : []

  // Clamp the index when images get filtered out (e.g. an onError marks one
  // broken). Prev/next handlers can then use raw modular arithmetic without
  // wrapping to the wrong slot.
  useEffect(() => {
    if (allImages.length > 0 && currentImageIndex >= allImages.length) {
      setCurrentImageIndex(allImages.length - 1)
    }
  }, [allImages.length, currentImageIndex])

  const currentImageUrl = allImages[currentImageIndex] ?? allImages[0]

  // Filter templates by search query
  const filteredTemplates = templates.filter((t) => {
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    const name = (t.display_name || t.name).toLowerCase()
    const oneLiner = (t.template_one_liner || '').toLowerCase()
    return name.includes(q) || oneLiner.includes(q)
  })

  // --- Render ---

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-8 h-8 animate-spin mb-3" />
        <p className="text-sm">Loading templates...</p>
      </div>
    )
  }

  if (error && viewMode === 'grid') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <AlertCircle className="w-8 h-8 mb-3 text-destructive" />
        <p className="text-sm text-destructive">{error}</p>
        <button
          onClick={loadTemplates}
          className="mt-3 text-sm text-primary hover:underline"
        >
          Try again
        </button>
      </div>
    )
  }

  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <LayoutTemplate className="w-10 h-10 mb-3" />
        <p className="text-sm font-medium">No templates available yet</p>
        <p className="text-xs mt-1">Check back later for pre-built app templates</p>
      </div>
    )
  }

  // Detail view
  if (viewMode === 'detail') {
    if (detailLoading || !selectedTemplate) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin mb-3" />
          <p className="text-sm">Loading template details...</p>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {/* Image carousel — full width on top */}
        {allImages.length > 0 && (
          <div className="relative rounded-lg overflow-hidden border border-border bg-secondary/30 aspect-[2/1]">
            <img
              src={currentImageUrl}
              alt={`Preview ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
              onError={() => markBroken(currentImageUrl)}
            />
            {allImages.length > 1 && (
              <>
                <button
                  onClick={() =>
                    setCurrentImageIndex((i) => (i - 1 + allImages.length) % allImages.length)
                  }
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() =>
                    setCurrentImageIndex((i) => (i + 1) % allImages.length)
                  }
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {allImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      aria-label={`Go to image ${idx + 1}`}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* 70/30 split: description left, buttons right */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left 70%: description */}
          <div className="w-full lg:flex-[65] min-w-0">
            {selectedTemplate.template_description ? (
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                {selectedTemplate.template_description}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground/50 italic">No description</p>
            )}
          </div>

          {/* Right 30%: action panel */}
          <div className="w-full lg:flex-[35] space-y-3">
            {/* Use This Template button / form */}
            {!showNameInput ? (
              <button
                onClick={() => setShowNameInput(true)}
                className="w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium text-sm"
              >
                Use This Template
              </button>
            ) : (
              <form onSubmit={handleUseTemplate} className="space-y-2">
                <div>
                  <label htmlFor="template-app-name" className="block text-xs font-medium mb-0.5">App Name</label>
                  <input
                    id="template-app-name"
                    type="text"
                    value={appDisplayName}
                    onChange={(e) => setAppDisplayName(e.target.value)}
                    placeholder="My New App"
                    autoFocus
                    maxLength={60}
                    className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label htmlFor="template-url-slug" className="block text-xs font-medium mb-0.5">URL Slug</label>
                  <div className="relative">
                    <input
                      id="template-url-slug"
                      type="text"
                      value={slugInput}
                      onChange={(e) => {
                        setSlugInput(normalizeAppSlugInput(e.target.value))
                        setSlugEdited(true)
                      }}
                      placeholder="my-new-app"
                      minLength={3}
                      maxLength={50}
                      className="w-full px-2.5 py-1.5 pr-7 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      {isNameValid ? (
                        <Check className="w-3.5 h-3.5 text-green-500" />
                      ) : appName.length >= 3 ? (
                        <X className="w-3.5 h-3.5 text-destructive" />
                      ) : null}
                    </div>
                  </div>
                  {appName && !isValidAppSlug(appName) && (
                    <p className="text-xs text-destructive mt-1">
                      3-50 chars, starts with letter, lowercase + numbers + hyphens
                    </p>
                  )}
                </div>
                {createError && (
                  <p className="text-xs text-destructive">{createError}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowNameInput(false); setCreateError(null) }}
                    className="flex-1 px-2.5 py-1.5 text-sm rounded-lg border border-border hover:bg-secondary transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating || !isNameValid}
                    className="flex-1 px-2.5 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                  >
                    {creating ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating...</>
                    ) : 'Create'}
                  </button>
                </div>
              </form>
            )}

            {/* Live Preview button */}
            <button
              onClick={handleLivePreview}
              disabled={previewLoading}
              className="w-full px-4 py-2.5 rounded-lg border border-border hover:bg-secondary transition-colors font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {previewLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Starting preview...</>
              ) : selectedTemplate.status === 'running' ? (
                <><ExternalLink className="w-4 h-4" /> Live Preview</>
              ) : (
                <><Play className="w-4 h-4" /> Live Preview</>
              )}
            </button>

            {previewError && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                {previewError}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Grid view
  return (
    <div className="space-y-4">
      {onClose && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-muted-foreground">
            Choose a template to get started
          </h3>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {filteredTemplates.map((template) => {
          const heroUrl = template.template_hero_image
          const showHero = heroUrl && !isBroken(heroUrl)
          return (
          <button
            key={template.id}
            onClick={() => openDetail(template)}
            className="group text-left rounded-lg border border-border bg-card hover:border-primary/50 hover:shadow-md transition-all overflow-hidden"
          >
            {/* Hero image */}
            <div className="aspect-[16/10] bg-secondary/50 overflow-hidden">
              {showHero ? (
                <img
                  src={heroUrl}
                  alt={template.display_name || template.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={() => markBroken(heroUrl)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                  <LayoutTemplate className="w-10 h-10 text-primary/30" />
                </div>
              )}
            </div>

            {/* Card body */}
            <div className="p-3">
              <p className="font-medium text-sm truncate">
                {template.display_name || template.name}
              </p>
              {template.template_one_liner && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                  {template.template_one_liner}
                </p>
              )}
            </div>
          </button>
          )
        })}
      </div>
    </div>
  )
}
